/**
 * Intercom Service - Frontend API Client
 *
 * Handles communication with the backend Intercom endpoints.
 * Designed for easy expansion to support future Intercom features.
 *
 * CURRENT FEATURES:
 * - Submit access applications
 *
 * FUTURE FEATURES:
 * - Initialize messenger widget
 * - Identify logged-in users
 * - Track events
 * - Open/close messenger programmatically
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

// ============================================================================
// TYPES
// ============================================================================

export interface ApplicationData {
  firstName?: string
  lastName?: string
  name?: string  // Legacy or generated from firstName + lastName
  email: string
  reason: string
  phoneNumber?: string
  phoneVerified?: boolean
  phoneVerificationSkipped?: boolean
  applicationId?: string // Optional: If provided, updates existing application
}

export interface SubmitResult {
  success: boolean
  message?: string
  error?: string
  applicationId?: string
  applicationNumber?: number
  conversationId?: string
  userHash?: string
}

export interface ExpeditedReviewData {
  applicationId: string
  email: string
  isCarynArmyMember: boolean
  carynArmyUsername?: string
  dob: string
  instagram: string
  twitter: string
  snapchat: string
  zipCode: string
  profilePictureUrl: string | null
  openToMeetups: boolean
  hobbies: string
  tenThousandAnswer: string
  bodyType: string
  heightFeet: string
  heightInches: number
  timeTaken?: number | null
}

// Future types for expansion
export interface IntercomConfig {
  appId: string
  customLauncherSelector?: string
  hideDefaultLauncher?: boolean
}

export interface IntercomUser {
  userId: string
  email: string
  name?: string
  createdAt?: number
  userHash?: string  // HMAC for identity verification
}

// ============================================================================
// APPLICATION SUBMISSION
// ============================================================================

/**
 * Submit access application
 * Called when user fills out the Apply form
 *
 * @param data - Application form data (name, email, reason)
 * @returns Result with success status and message/error
 */
export async function submitApplication(data: ApplicationData): Promise<SubmitResult> {
  try {
    // Use auth-service endpoint (always running) instead of intercom/customer-service
    const response = await fetch(`${API_URL}/api/auth/submit-application`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        firstName: data.firstName,
        lastName: data.lastName,
        name: data.name,
        email: data.email,
        reason: data.reason,
        phoneNumber: data.phoneNumber,
        phoneVerified: data.phoneVerified,
        phoneVerificationSkipped: data.phoneVerificationSkipped,
        applicationId: data.applicationId
      })
    })

    const result = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: result.error || 'Failed to submit application'
      }
    }

    return {
      success: true,
      message: result.message,
      applicationId: result.applicationId,
      applicationNumber: result.applicationNumber
    }

  } catch (error) {
    console.error('[IntercomService] Submit application error:', error)
    return {
      success: false,
      error: 'Network error. Please check your connection and try again.'
    }
  }
}

/**
 * Submit access application with conversation ID and user hash
 * Enhanced version that returns data needed for Intercom handoff
 *
 * @param data - Application form data (name, email, reason)
 * @returns Result with success status, conversationId, and userHash
 */
export async function submitApplicationWithConversation(data: ApplicationData): Promise<SubmitResult> {
  try {
    const response = await fetch(`${API_URL}/api/intercom/applications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: data.name,
        email: data.email,
        reason: data.reason,
        applicationId: data.applicationId // Include applicationId for updates
      })
    })

    const result = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: result.error || 'Failed to submit application'
      }
    }

    return {
      success: true,
      message: result.message,
      applicationId: result.applicationId,
      conversationId: result.conversationId
    }

  } catch (error) {
    console.error('[IntercomService] Submit application error:', error)
    return {
      success: false,
      error: 'Network error. Please check your connection and try again.'
    }
  }
}

/**
 * Get identity hash for Intercom user verification
 * The hash is an HMAC-SHA256 of the email, generated server-side
 *
 * @param email - User email to hash
 * @returns Result with userHash for Intercom identity verification
 */
export async function getIdentityHash(email: string): Promise<{ success: boolean; userHash?: string; error?: string }> {
  try {
    const response = await fetch(`${API_URL}/api/intercom/identity-hash`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    })

    const result = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: result.error || 'Failed to get identity hash'
      }
    }

    return {
      success: true,
      userHash: result.userHash
    }

  } catch (error) {
    console.error('[IntercomService] Get identity hash error:', error)
    return {
      success: false,
      error: 'Network error'
    }
  }
}

// ============================================================================
// EXPEDITED REVIEW
// ============================================================================

/**
 * Get application number for a given application ID
 * Used by returning applicants to display their number
 */
export async function getApplicationNumber(applicationId: string): Promise<{ applicationNumber: number; expeditedReviewSubmitted: boolean } | null> {
  try {
    const response = await fetch(`${API_URL}/api/auth/application-number?applicationId=${encodeURIComponent(applicationId)}`)
    const result = await response.json()

    if (!response.ok || !result.success) {
      return null
    }

    return {
      applicationNumber: result.applicationNumber,
      expeditedReviewSubmitted: result.expeditedReviewSubmitted || false
    }
  } catch (error) {
    console.error('[IntercomService] Get application number error:', error)
    return null
  }
}

/**
 * Submit expedited review form data
 * Stores additional info in application metadata for priority review
 */
export async function submitExpeditedReview(data: ExpeditedReviewData): Promise<SubmitResult> {
  try {
    const response = await fetch(`${API_URL}/api/auth/submit-expedited-review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    const result = await response.json()

    if (!response.ok) {
      return { success: false, error: result.error || 'Failed to submit expedited review' }
    }

    return { success: true, message: result.message }
  } catch (error) {
    console.error('[IntercomService] Submit expedited review error:', error)
    return { success: false, error: 'Network error. Please check your connection and try again.' }
  }
}

// ============================================================================
// FUTURE: MESSENGER WIDGET
// ============================================================================

/**
 * Initialize Intercom messenger widget
 * Call this in App.tsx or a provider component
 *
 * @param config - Intercom configuration
 */
export function initializeMessenger(_config: IntercomConfig): void {
  // TODO: Implement when adding messenger widget
  // This will load the Intercom script and configure it
  console.warn('[IntercomService] Messenger not yet implemented')
}

/**
 * Identify logged-in user to Intercom
 * Call this after user authentication
 *
 * @param user - User identity data
 */
export function identifyUser(_user: IntercomUser): void {
  // TODO: Implement when adding user identification
  console.warn('[IntercomService] User identification not yet implemented')
}

/**
 * Track custom event
 *
 * @param eventName - Name of the event
 * @param metadata - Additional event data
 */
export function trackEvent(_eventName: string, _metadata?: Record<string, unknown>): void {
  // TODO: Implement when adding event tracking
  console.warn('[IntercomService] Event tracking not yet implemented')
}

/**
 * Open messenger widget
 */
export function openMessenger(): void {
  // TODO: Implement
  console.warn('[IntercomService] Messenger controls not yet implemented')
}

/**
 * Close messenger widget
 */
export function closeMessenger(): void {
  // TODO: Implement
  console.warn('[IntercomService] Messenger controls not yet implemented')
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  submitApplication,
  submitApplicationWithConversation,
  getIdentityHash,
  getApplicationNumber,
  submitExpeditedReview,
  initializeMessenger,
  identifyUser,
  trackEvent,
  openMessenger,
  closeMessenger
}
